import { AutomateTask, success } from "@iflyrpa/share";
import { Http } from "../../utils/http";
import { type FetchArticlesParams } from ".";
import { errorResponse, FetchArticlesResponse, ToutiaoArticle } from "./types";

interface ArticleResponse {
    itemCell: {
        actionCtrl: Record<string, unknown>;
        cellCtrl: {
            articleImageType: number;
            dspStyle: null | string;
        };
        extra: {
            answer_count: string;
            origin_content: string;
        };
        logPB: string;
        articleBase: {
            createTime: number;
            itemStatus: number;
            articleURL: string;
            displayFlags: string;
            gidStr: string;
            subTitle: string | null;
            title: string;
            abstractText: string;
            content: string;
            displayURL: string;
            groupID: number;
            publishTime: number;
            schema: string;
            visibilityLevel: number;
        };
        articleClassification: {
            bizTag: number;
            groupSource: number;
            isOriginal: boolean;
            isStick: boolean;
            isSubject: boolean;
            articleSubType: number;
            articleType: number;
            bizID: number;
        };
        containsElements: {
            hasImage: boolean;
            hasVideo: boolean;
        };
        imageList: {
            staggerFeedCover: ImageInfo[];
            thumbImageList: ImageInfo[];
        };
        itemCounter: {
            readCount: number;
            repinCount: number;
            showCount: number;
            videoWatchCount: number;
            commentCount: number;
            diggCount: number;
        };
        richContentInfo: {
            richContent: string;
            titleRichSpan: string;
        };
        shareInfo: {
            shareURL: string;
        };
        userInfo: {
            avatarURL: string;
            name: string;
            userID: number;
        };
    };
}

interface ImageInfo {
    width: number;
    height: number;
    uri: string;
    url: string;
}

export async function handleToutiaoData(
    _task: AutomateTask,
    params: FetchArticlesParams,
): Promise<FetchArticlesResponse> {
    try {
        const {
            cookies,
            pageNum = 1,
            pageSize = 10,
            showOriginalData = false,
            onlySuccess = false,
            containsArticle = false,
        } = params;

        const http = new Http({
            headers: {
                cookie: cookies.map((it) => `${it.name}=${it.value}`).join(";"),
            },
        });

        const getDetailContent = async (id: string) => {
            try {
                const detailInfo = await http.api<{ content: string }>({
                    method: "get",
                    url: "https://mp.toutiao.com/mp/agw/article/edit",
                    params: {
                        pgc_id: id,
                        wxstyle: 0,
                        format: "json",
                    },
                });

                return detailInfo.content;
            } catch (err) {
                console.error("❌ JSON 解析失败:", err);
                return null;
            }
        };

        const visitedUid = await http.api<{ code: number; user_id_str: string }>({
            url: "https://mp.toutiao.com/mp/agw/creator_center/user_info?",
            method: "get",
            params: { app_id: 1231 },
        });

        if (visitedUid.code !== 0) {
            return errorResponse("头条号账号信息获取失败，请检查账号状态", 414);
        }

        const articleInfo = await http.api<{
            data: { assembleCell: ArticleResponse }[];
            total_number: number;
            api_base_info: { app_extra_params: string };
        }>({
            method: "get",
            url: "https://mp.toutiao.com/api/feed/mp_provider/v1/",
            params: {
                provider_type: "mp_provider",
                aid: "13",
                app_name: "news_article",
                category: "mp_all",
                channel: "",
                stream_api_version: "88",
                genre_type_switch: '{"repost":1,"small_video":1,"toutiao_graphic":1,"weitoutiao":1,"xigua_video":1}',
                device_platform: "pc",
                platform_id: "0",
                visited_uid: visitedUid.user_id_str,
                offset: (pageNum - 1) * pageSize,
                count: pageSize,
                keyword: "",
                client_extra_params: `{"category":"mp_all","real_app_id":"1231","need_forward":"true","offset_mode":"1","page_index":"1","status":${onlySuccess ? "2" : "8"},"source":"0"}`,
                app_id: "1231",
            },
        });

        const articleCell: ToutiaoArticle[] = await Promise.all(
            articleInfo.data.map(async (item) => ({
                title: item.assembleCell.itemCell.articleBase.title,
                imageUrl: item.assembleCell.itemCell.imageList.staggerFeedCover[0].url,
                createTime: item.assembleCell.itemCell.articleBase.publishTime,
                redirectUrl: item.assembleCell.itemCell.articleBase.articleURL,
                showNum: item.assembleCell.itemCell.itemCounter.showCount,
                readNum: item.assembleCell.itemCell.itemCounter.readCount,
                likeNum: item.assembleCell.itemCell.itemCounter.diggCount,
                commentNum: item.assembleCell.itemCell.itemCounter.commentCount,
                ...(showOriginalData ? { originalData: item } : {}),
                ...(containsArticle
                    ? { article: await getDetailContent(item.assembleCell.itemCell.articleBase.gidStr) }
                    : {}),
            })),
        );

        const api_base_info_json: { total_count: number } = JSON.parse(articleInfo.api_base_info.app_extra_params);
        return success(
            {
                articleCell,
                ...(onlySuccess
                    ? {
                          pagination: {
                              total: api_base_info_json.total_count,
                              pageNum,
                              pageSize,
                          },
                      }
                    : null),
            },
            "头条号文章文章获取成功",
        );
    } catch (error) {
        return errorResponse(error instanceof Error ? error.message : "头条号文章数据获取失败");
    }
}
